import axios from 'axios';

/**
 * Classification FRD — Phase 2: pivot-style grouping for the IBOR position
 * register. The register groups by any ordered combination of Level 1 /
 * Level 2 / Credit Quality with securities as leaves, so group totals are
 * computed from exactly the rows the flat register shows and FR-12
 * reconciliation is inherent (zero difference by construction).
 *
 * AG Grid row grouping is an Enterprise feature; on Community we flatten the
 * group tree into ordinary rows and manage expansion state ourselves.
 */

export type PivotDimension = 'L1' | 'L2' | 'QUALITY';

// The IBOR register reopens in the last-used grouping (per browser).
const PIVOT_DIMS_STORAGE_KEY = 'axiomflow:ibor:pivotDims';

export const loadStoredPivotDims = (): PivotDimension[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(PIVOT_DIMS_STORAGE_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((d): d is PivotDimension => d === 'L1' || d === 'L2' || d === 'QUALITY');
  } catch {
    return [];
  }
};

export const storePivotDims = (dims: PivotDimension[]): void => {
  try {
    localStorage.setItem(PIVOT_DIMS_STORAGE_KEY, JSON.stringify(dims));
  } catch {
    // localStorage unavailable (private mode etc.) — grouping just won't persist.
  }
};

export const PIVOT_DIMENSION_LABELS: Record<PivotDimension, string> = {
  L1: 'Level 1',
  L2: 'Level 2',
  QUALITY: 'Credit Quality',
};

export interface SecurityClassification {
  l1Name?: string;
  l2Name?: string;
  qualityBucket?: string;
  qualityLabel?: string;
  rating?: string;
}

export const fetchClassifications = async (
  securityIds: number[],
  validAsOf: string,
  knowledgeAsOf: string,
): Promise<Map<number, SecurityClassification>> => {
  const map = new Map<number, SecurityClassification>();
  if (securityIds.length === 0) return map;
  const response = await axios.post('/api/classification/resolve', {
    securityIds,
    validAsOf: validAsOf || 'now',
    knowledgeAsOf: knowledgeAsOf || 'now',
  });
  for (const row of (Array.isArray(response.data) ? response.data : [])) {
    map.set(Number(row.securityId), {
      l1Name: row.l1Name,
      l2Name: row.l2Name,
      qualityBucket: row.quality?.bucket,
      qualityLabel: row.quality?.label,
      rating: row.quality?.rating,
    });
  }
  return map;
};

interface PivotablePosition {
  securityId: number;
  cusip?: string | null;
  isin?: string | null;
  securityName?: string | null;
  portfolioName?: string | null;
  portfolioCode?: string | null;
  assetClassName?: string | null;
  assetClass?: string | null;
  quantity: number;
  parAmount: number;
  unfundedCommitment?: number;
  marketValue: number;
  costBasis: number;
  unrealizedPnl: number;
  realizedPnl: number;
}

export interface PivotGroupFields {
  __group: true;
  __groupKey: string;
  __depth: number;
  __expanded: boolean;
  __count: number;
  __pct: number;
  __unclassified: boolean;
}

export type PivotRow<T> = (T & { __depth?: number }) | (Partial<T> & PivotGroupFields & { securityName: string });

export const UNCLASSIFIED_LABEL = 'Unclassified';

const fallbackClassification = (row?: Partial<PivotablePosition>): SecurityClassification | undefined => {
  const family = String(row?.assetClassName || row?.assetClass || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toUpperCase()
    .replace(/[_-]/g, ' ');
  if (family.includes('LEVERAGED LOAN') || family.includes('TERM LOAN')) {
    return {
      l1Name: 'Public Credit',
      l2Name: 'Leveraged Loan',
      qualityLabel: 'Not Rated',
    };
  }
  if (family.includes('ASSET BASED FINANCE')) {
    return {
      l1Name: 'Private Credit',
      l2Name: 'Asset-Based Finance',
      qualityLabel: 'Not Rated',
    };
  }
  return undefined;
};

const isMissingClassificationValue = (value?: string | null): boolean => {
  const normalized = String(value ?? '').trim().toUpperCase();
  return (
    !normalized ||
    normalized === UNCLASSIFIED_LABEL.toUpperCase() ||
    normalized === 'UNKNOWN' ||
    normalized === 'N/A' ||
    normalized === 'NA'
  );
};

const pickClassificationValue = (
  primary?: string | null,
  fallback?: string | null,
): string | undefined => {
  if (isMissingClassificationValue(primary)) {
    return isMissingClassificationValue(fallback) ? undefined : String(fallback).trim();
  }
  return String(primary).trim();
};

const joinedUnique = <T extends PivotablePosition>(
  rows: T[],
  pick: (row: T) => unknown,
): string | null => {
  const values = Array.from(new Set(
    rows
      .map(pick)
      .map(value => String(value ?? '').trim())
      .filter(Boolean),
  ));
  return values.length > 0 ? values.join(' ') : null;
};

export const dimensionValue = (
  cls: SecurityClassification | undefined,
  dim: PivotDimension,
  row?: Partial<PivotablePosition>,
): string => {
  const fallback = fallbackClassification(row);
  const resolved: SecurityClassification = {
    l1Name: pickClassificationValue(cls?.l1Name, fallback?.l1Name),
    l2Name: pickClassificationValue(cls?.l2Name, fallback?.l2Name),
    qualityBucket: pickClassificationValue(cls?.qualityBucket, fallback?.qualityBucket),
    qualityLabel: pickClassificationValue(cls?.qualityLabel, fallback?.qualityLabel),
    rating: pickClassificationValue(cls?.rating, fallback?.rating),
  };
  if (dim === 'L1') return resolved?.l1Name || UNCLASSIFIED_LABEL;
  if (dim === 'L2') return resolved?.l2Name || (resolved?.l1Name ? 'No Level 2' : UNCLASSIFIED_LABEL);
  return resolved?.qualityLabel || 'Not Rated';
};

/**
 * Flattens positions into group rows + leaf rows for the register grid.
 * Groups are ordered by market value (desc, Unclassified last); collapsed
 * groups contribute a single aggregate row.
 */
export function buildPivotRows<T extends PivotablePosition>(
  positions: T[],
  classifications: Map<number, SecurityClassification>,
  dims: PivotDimension[],
  expandedKeys: Set<string>,
): PivotRow<T>[] {
  const out: PivotRow<T>[] = [];
  // Classification weights are exposure shares, not shares of net NAV. Using
  // signed net market value lets a large negative cash balance collapse the
  // denominator and produces impossible values above 100% for credit groups.
  const grossMarketValue = positions.reduce(
    (sum, p) => sum + Math.abs(Number(p.marketValue || 0)),
    0,
  );

  const walk = (rows: T[], depth: number, parentKey: string) => {
    if (depth === dims.length) {
      rows.forEach(r => out.push({ ...r, __depth: depth }));
      return;
    }
    const dim = dims[depth];
    const buckets = new Map<string, T[]>();
    for (const row of rows) {
      const label = dimensionValue(classifications.get(row.securityId), dim, row);
      const bucket = buckets.get(label);
      if (bucket) bucket.push(row); else buckets.set(label, [row]);
    }
    const entries = Array.from(buckets.entries()).map(([label, members]) => ({
      label,
      members,
      marketValue: members.reduce((sum, p) => sum + Number(p.marketValue || 0), 0),
    }));
    entries.sort((a, b) => {
      const aUn = a.label === UNCLASSIFIED_LABEL ? 1 : 0;
      const bUn = b.label === UNCLASSIFIED_LABEL ? 1 : 0;
      return aUn - bUn || b.marketValue - a.marketValue;
    });
    for (const entry of entries) {
      const key = `${parentKey}/${dim}:${entry.label}`;
      const expanded = expandedKeys.has(key);
      const sum = (pick: (p: T) => number | undefined) =>
        entry.members.reduce((acc, p) => acc + Number(pick(p) || 0), 0);
      // FR-13: par/quantity/unfunded are unit measures — summing them across
      // unlike instrument families (bond par + property units + cash) is a
      // misleading total. Only sum when the group is a single family; mixed
      // groups render '—'. Currency measures (MV, cost, P&L) always sum.
      const families = new Set(entry.members.map(p => p.assetClassName || p.assetClass || 'UNKNOWN'));
      const singleFamily = families.size === 1;
      out.push({
        __group: true,
        __groupKey: key,
        __depth: depth,
        __expanded: expanded,
        __count: entry.members.length,
        __pct: grossMarketValue !== 0
          ? (entry.members.reduce((sum, p) => sum + Math.abs(Number(p.marketValue || 0)), 0) / grossMarketValue) * 100
          : 0,
        __unclassified: entry.label === UNCLASSIFIED_LABEL,
        securityName: entry.label,
        // AG Grid's quick filter only evaluates the rendered group row when a
        // group is collapsed. Carry member identifiers onto the group row so a
        // CUSIP/security search still reveals the Level 1/Level 2 parent.
        cusip: joinedUnique(entry.members, p => p.cusip),
        isin: joinedUnique(entry.members, p => p.isin),
        portfolioName: joinedUnique(entry.members, p => p.portfolioName),
        portfolioCode: joinedUnique(entry.members, p => p.portfolioCode),
        assetClass: joinedUnique(entry.members, p => p.assetClass || p.assetClassName),
        quantity: singleFamily ? sum(p => p.quantity) : null,
        parAmount: singleFamily ? sum(p => p.parAmount) : null,
        unfundedCommitment: singleFamily ? sum(p => p.unfundedCommitment) : null,
        marketValue: entry.marketValue,
        costBasis: sum(p => p.costBasis),
        unrealizedPnl: sum(p => p.unrealizedPnl),
        realizedPnl: sum(p => p.realizedPnl),
      } as PivotRow<T>);
      if (expanded) walk(entry.members, depth + 1, key);
    }
  };

  walk(positions, 0, '');
  return out;
}
